import { useCallback, useMemo, useState } from 'react'
import { getAddress, maxUint256, type Address } from 'viem'
import { useAccount, usePublicClient, useReadContracts, useSignTypedData, useWriteContract } from 'wagmi'
import { MAX_UINT160, erc20Abi, permit2Abi } from '../lib/abis'
import { SWAP_DEFAULTS, config } from '../lib/config'
import {
  PERMIT_EXPIRATION_SECONDS,
  PERMIT_SIG_DEADLINE_SECONDS,
  permit2Domain,
  permitSingleTypes,
  type SignedPermit,
} from '../lib/permit2'
import { isNative } from '../lib/tokens'

/**
 * The two allowances an Omega swap spends through, and how each is granted.
 *
 * `OmegaEncoder` opens every non-native trade with `PERMIT2_TRANSFER_FROM`, so the router
 * never touches the user's ERC20 allowance directly — Permit2 does, on its behalf. That
 * makes the chain of custody:
 *
 *   token.allowance(user -> Permit2)            an ordinary ERC20 approval: a transaction
 *   Permit2.allowance(user, token -> router)    Permit2's own ledger: a signature
 *
 * Both must hold when `execute` runs. Missing either one reverts inside the router, after
 * the user has signed and paid gas.
 *
 * ### The Permit2 half is signed, not sent
 *
 * The user signs a `PermitSingle` (no gas), this hook holds it, and `useSwap` hands it to
 * `/swap`, which opens the plan with `PERMIT2_PERMIT` so the allowance lands inside the swap
 * transaction itself. A first swap of a token is therefore two transactions (the ERC20
 * approval, then the swap), not three. The permit sets `type(uint160).max` for
 * `PERMIT_EXPIRATION_SECONDS`, so after it lands the on-chain allowance covers later swaps
 * and no signature is asked for until it nears expiry.
 *
 * The ERC20 half cannot be a signature: it is the token's own `approve`, and Omega has no
 * command for EIP-2612 `permit`.
 *
 * ### Reading the allowance, and when a held signature still counts
 *
 * A Permit2 allowance carries a `uint48` expiration, and a generous amount with a past
 * timestamp spends nothing, so both are read. A held signature counts only while it can
 * still be applied: same owner and token, the nonce Permit2 currently expects (a permit that
 * already landed has consumed it), enough amount, and a signature deadline that outlasts any
 * swap deadline the API would accept. Otherwise the step comes back and the user signs again.
 *
 * ### Native FLR needs neither
 *
 * A native input pays the router with the transaction's value and the plan opens with
 * `WRAP_ETH` instead of `PERMIT2_TRANSFER_FROM`, so there is no allowance anywhere to read.
 * `needs` is an empty list rather than null for it: null means "not known yet".
 */

export type ApprovalStep = 'erc20' | 'permit2'

export type ApprovalState = {
  /** Null while the reads are in flight or no wallet is connected. */
  needs: ApprovalStep[] | null
  isLoading: boolean
  /** The step currently being submitted, mined or signed. */
  pending: ApprovalStep | null
  error: Error | null
  /** `erc20` sends the token approval; `permit2` asks the wallet to sign the permit. */
  approve: (step: ApprovalStep) => Promise<void>
  refetch: () => void
  /**
   * The signed permit `/swap` must carry, or null when the Permit2 allowance is already on
   * chain — or when there is none to carry yet, in which case `needs` includes `permit2`.
   */
  permit: SignedPermit | null
  /** Forget the held signature. Called once a swap has landed and consumed its nonce. */
  clearPermit: () => void
  /** Present once read: what Permit2 currently allows the router, until when, and its nonce. */
  permit2Allowance: { amount: bigint; expiration: number; nonce: number } | null
}

/**
 * How much of the Permit2 expiry window must remain for an on-chain allowance to count.
 *
 * An allowance expiring in ten seconds passes a naive `expiration > now` check and then
 * fails in the swap. The swap deadline itself is at most 30 minutes
 * (`swap.maxDeadlineSeconds`), so anything inside an hour is re-signed rather than raced.
 */
const MIN_REMAINING_SECONDS = 3_600

/**
 * How long a held signature must still be submittable. The API refuses a permit whose
 * signature lapses before the swap's deadline, which may be `maxDeadlineSeconds` out; the
 * extra minute absorbs clock skew between this browser and the API.
 */
const MIN_SIG_REMAINING_SECONDS = SWAP_DEFAULTS.maxDeadlineSeconds + 60

type HeldPermit = { owner: Address; permit: SignedPermit }

export function useApprovals(token: Address | null, amountIn: bigint | null): ApprovalState {
  const { address: owner } = useAccount()
  const publicClient = usePublicClient()
  const { writeContractAsync } = useWriteContract()
  const { signTypedDataAsync } = useSignTypedData()
  const [pending, setPending] = useState<ApprovalStep | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [held, setHeld] = useState<HeldPermit | null>(null)

  const native = token !== null && isNative(token)
  const enabled = Boolean(owner && token) && !native

  const reads = useReadContracts({
    allowFailure: false,
    contracts: [
      {
        address: (token ?? undefined) as Address,
        abi: erc20Abi,
        functionName: 'allowance',
        args: [owner as Address, config.permit2],
      },
      {
        address: config.permit2,
        abi: permit2Abi,
        functionName: 'allowance',
        args: [owner as Address, token as Address, config.router],
      },
    ],
    query: { enabled, refetchInterval: 15_000 },
  })

  const permit2Allowance = useMemo(() => {
    const entry = reads.data?.[1]
    if (!entry) return null
    const [amount, expiration, nonce] = entry as readonly [bigint, number, number]
    return { amount, expiration: Number(expiration), nonce: Number(nonce) }
  }, [reads.data])

  // Recomputed on every allowance read (every 15s), which is also what ages a held
  // signature out: its deadline is compared against the clock here, not once at signing.
  const { needs, permit } = useMemo<{ needs: ApprovalStep[] | null; permit: SignedPermit | null }>(() => {
    if (native && owner) return { needs: [], permit: null }
    if (!enabled || !reads.data || amountIn === null || !permit2Allowance || !token || !owner) {
      return { needs: null, permit: null }
    }
    const nowSeconds = Math.floor(Date.now() / 1000)
    const steps: ApprovalStep[] = []

    const erc20Allowance = reads.data[0] as bigint
    if (erc20Allowance < amountIn) steps.push('erc20')

    const onChain =
      permit2Allowance.amount >= amountIn && permit2Allowance.expiration >= nowSeconds + MIN_REMAINING_SECONDS
    if (onChain) return { needs: steps, permit: null }

    const signed = held?.permit
    const usable =
      held !== null &&
      signed !== undefined &&
      held.owner === owner &&
      signed.details.token === getAddress(token) &&
      signed.details.nonce === permit2Allowance.nonce &&
      signed.details.amount >= amountIn &&
      signed.sigDeadline >= BigInt(nowSeconds + MIN_SIG_REMAINING_SECONDS)
    if (!usable) steps.push('permit2')
    return { needs: steps, permit: usable ? signed : null }
  }, [native, owner, enabled, reads.data, amountIn, permit2Allowance, token, held])

  const approve = useCallback(
    async (step: ApprovalStep) => {
      if (!token || !owner || native) return
      setPending(step)
      setError(null)
      try {
        if (step === 'erc20') {
          const hash = await writeContractAsync({
            address: token,
            abi: erc20Abi,
            functionName: 'approve',
            args: [config.permit2, maxUint256],
          })
          await publicClient?.waitForTransactionReceipt({ hash })
          await reads.refetch()
          return
        }

        // The nonce is read fresh rather than from the last poll: signing over a nonce a
        // permit has since consumed produces a signature Permit2 rejects.
        const fresh = await reads.refetch()
        const entry = fresh.data?.[1] as readonly [bigint, number, number] | undefined
        if (!entry) throw new Error('could not read the Permit2 nonce; try again')
        const nowSeconds = Math.floor(Date.now() / 1000)
        const message = {
          details: {
            token: getAddress(token),
            // type(uint160).max: Permit2 stops decrementing at that value, so one permit
            // covers every swap until it expires rather than just this one.
            amount: MAX_UINT160,
            expiration: nowSeconds + PERMIT_EXPIRATION_SECONDS,
            nonce: Number(entry[2]),
          },
          spender: config.router,
          sigDeadline: BigInt(nowSeconds + PERMIT_SIG_DEADLINE_SECONDS),
        }
        const signature = await signTypedDataAsync({
          domain: permit2Domain,
          types: permitSingleTypes,
          primaryType: 'PermitSingle',
          message,
        })
        setHeld({ owner, permit: { ...message, signature } })
      } catch (cause) {
        setError(cause instanceof Error ? cause : new Error(String(cause)))
      } finally {
        setPending(null)
      }
    },
    [token, owner, native, writeContractAsync, signTypedDataAsync, publicClient, reads]
  )

  // Stable, so a caller can depend on them in an effect without re-running it every render.
  const { refetch: refetchReads } = reads
  const refetch = useCallback(() => void refetchReads(), [refetchReads])
  const clearPermit = useCallback(() => setHeld(null), [])

  return {
    needs,
    isLoading: reads.isLoading,
    pending,
    error,
    approve,
    refetch,
    permit,
    clearPermit,
    permit2Allowance,
  }
}
